import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import {
  CSSProperties,
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  FadeEdge,
  ScrollArrow,
  ScrollerWrapper,
  ScrollViewport,
} from "./style";

interface IHorizontalScrollerProps {
  className?: string;
  style?: CSSProperties;
  /** Show arrow buttons when scrollable. Default true. */
  showArrows?: boolean;
  /** Show fade gradient on overflow edges. Default true. */
  showFadeEdges?: boolean;
  /** Hide the native scrollbar (default: shown as a thin track). */
  hideScrollbar?: boolean;
  /** Override pixel distance per arrow click. Default = 60% of viewport width. */
  scrollByPx?: number;
}

/**
 * Wraps a horizontally-scrollable row of items.
 *
 * - Auto-detects overflow via ResizeObserver + scroll event.
 * - Shows arrow buttons / fade edges ONLY when there's content to scroll to
 *   in that direction (never visible if everything fits).
 * - Hides the native scrollbar; arrow buttons drive scrolling on desktop,
 *   touch swipe on mobile.
 * - Uses a ref callback (not a useEffect) so listeners attach the moment
 *   the viewport DOM node mounts. This is the only correct way to wire
 *   listeners when the parent conditionally renders the scroller (return
 *   null when empty), since a regular useEffect with stable deps won't
 *   re-run when the node later appears.
 * - The wrapper enforces min-width: 0 + overflow: hidden so it never
 *   contributes max-content to a parent grid/flex track — meaning it
 *   cannot push siblings off-screen.
 */
function HorizontalScroller({
  children,
  className,
  style,
  showArrows = true,
  showFadeEdges = true,
  hideScrollbar = false,
  scrollByPx,
}: PropsWithChildren<IHorizontalScrollerProps>) {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const measure = useCallback(() => {
    const el = viewportRef.current;
    if (!el) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }
    const overflowing = el.scrollWidth - el.clientWidth > 1;
    if (!overflowing) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  const setViewportNode = useCallback(
    (node: HTMLDivElement | null) => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
      viewportRef.current = node;
      if (!node) {
        setCanScrollLeft(false);
        setCanScrollRight(false);
        return;
      }

      const onScroll = () => measure();
      node.addEventListener("scroll", onScroll, { passive: true });

      const ro = new ResizeObserver(() => measure());
      ro.observe(node);
      Array.from(node.children).forEach((child) => ro.observe(child));

      const mo = new MutationObserver(() => {
        Array.from(node.children).forEach((child) => ro.observe(child));
        measure();
      });
      mo.observe(node, { childList: true });

      measure();
      const raf = requestAnimationFrame(() => measure());

      cleanupRef.current = () => {
        node.removeEventListener("scroll", onScroll);
        ro.disconnect();
        mo.disconnect();
        cancelAnimationFrame(raf);
      };
    },
    [measure]
  );

  useEffect(() => {
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [measure]);

  const scrollBy = useCallback(
    (dir: 1 | -1) => {
      const el = viewportRef.current;
      if (!el) return;
      const distance = scrollByPx ?? Math.max(160, el.clientWidth * 0.6);
      el.scrollBy({ left: dir * distance, behavior: "smooth" });
    },
    [scrollByPx]
  );

  return (
    <ScrollerWrapper className={className} style={style}>
      {showFadeEdges && canScrollLeft && <FadeEdge edge="left" />}
      {showArrows && canScrollLeft && (
        <ScrollArrow
          type="button"
          edge="left"
          aria-label="Scroll left"
          onClick={() => scrollBy(-1)}
        >
          <CaretLeft size={12} weight="bold" />
        </ScrollArrow>
      )}
      <ScrollViewport ref={setViewportNode} $hideScrollbar={hideScrollbar}>
        {children}
      </ScrollViewport>
      {showFadeEdges && canScrollRight && <FadeEdge edge="right" />}
      {showArrows && canScrollRight && (
        <ScrollArrow
          type="button"
          edge="right"
          aria-label="Scroll right"
          onClick={() => scrollBy(1)}
        >
          <CaretRight size={12} weight="bold" />
        </ScrollArrow>
      )}
    </ScrollerWrapper>
  );
}

export default HorizontalScroller;
